// BOMModal.tsx — Ficha Técnica (BOM, lista de materiais).
//
// O que a ficha responde: "para produzir 1 unidade deste produto, o que entra e
// quanto". É o que liga venda a consumo de estoque.
//
// MULTINÍVEL: cada componente é OU um SKU OU OUTRA FICHA. Massa de pizza é uma
// ficha (farinha + água + fermento) que entra como componente da ficha da
// Pizza Margherita. Sem isso, a rede teria que repetir a receita da massa
// dentro de toda pizza — e corrigir farinha viraria corrigir trinta fichas.
//
// DISTINÇÃO NORMATIVA (a que mais gera erro de custo nesta tela):
//   • `percentualPerda` é perda DO PROCESSO e vive NA LINHA DA FICHA. É o que
//     se perde ao executar ESTA receita: a massa que fica na batedeira, o corte
//     da carne que vira aparo. Muda de receita para receita.
//   • `fatorRendimento` é perda DO INSUMO e vive NO SKU. É o que se perde ao
//     preparar o insumo, independente da receita: 1 kg de cebola com casca
//     rende 0,85 kg de cebola limpa. É o mesmo em toda ficha que use cebola.
// São DUAS coisas diferentes e NÃO se somam sozinhas. Quem preencher 10% aqui
// achando que está descrevendo a limpeza do insumo vai contar a perda duas
// vezes no custo — e nenhuma tela avisaria.
//
// O QUE GRAVA E O QUE NÃO GRAVA — leia antes de mexer aqui:
//   • O CABEÇALHO grava: `POST/PATCH suprimentos/fichas-tecnicas`, a tabela
//     `asup."FichaTecnica"`. Quem monta a requisição é o `handleSave` da
//     Configuração, e por isso o payload usa OS NOMES DE COLUNA do agregado
//     (`produtoSkuUuid`, `versao`, `vigenciaInicio`, `rendimentoPorcoes`,
//     `ativo`) — o servidor descarta em silêncio toda chave fora da lista
//     branca.
//   • OS COMPONENTES NÃO GRAVAM. A tabela `asup."FichaTecnicaItem"` existe no
//     banco, mas não há agregado no REST que escreva nela (`AGREGADOS` do
//     `suprimentos.service.ts` não a lista), e o `handleSave` do shell tira
//     `componentes` do payload e o descarta. Isso está DITO NA TELA, ao lado da
//     lista: uma ficha que perde a receita ao salvar e não avisa é pior que uma
//     que não salva.
//
// A LISTA de fichas ainda sai de `decryptedInfoToken.bomInfo` (DynamicTable
// case 7), coleção que nenhum backend produz — o cabeçalho gravado aqui não
// aparece na tabela até ela passar a ler o REST paginado.
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import * as AlfredoStyles from "../../../styles/components/configuration/modals/_casca";
import * as BOMStyles from "../../../styles/components/configuration/modals/BOMModal";
import { useModalAcessivel } from "../../../hooks/useModalAcessivel";

interface ModalPadraoProps {
  selectedObject: any;
  handleCloseModal: () => void;
  handleSave: (objectType: string, payload?: any) => any;
  parseInfoValue: (infoValue: string) => any;
  decryptedInfoToken?: any;
}

interface Componente {
  origem: "sku" | "ficha";
  componenteSkuUuid: string;
  componenteBomUuid: string;
  quantidade: string;
  unidade: string;
  percentualPerda: string;
}

const LINHA_VAZIA: Componente = {
  origem: "sku",
  componenteSkuUuid: "",
  componenteBomUuid: "",
  quantidade: "",
  unidade: "",
  percentualPerda: "",
};

const BOMModal: React.FC<ModalPadraoProps> = ({
  selectedObject,
  handleCloseModal,
  handleSave,
  parseInfoValue,
  decryptedInfoToken,
}) => {
  // Esc fecha, o foco fica preso dentro e volta para quem abriu, e o leitor
  // de tela anuncia a janela pelo titulo. Ver hooks/useModalAcessivel.ts.
  const { refDoModal, propsDoDialogo } = useModalAcessivel({
    aoFechar: handleCloseModal,
    idDoTitulo: "titulo-bommodal",
  });

  // `{}` é truthy (o useModal faz `setSelectedObject(object || {})`): quem diz
  // se é edição é o uuid, nunca a existência do objeto. `fichaUuid` vem
  // primeiro porque é a chave que o `handleSave` do shell lê para escolher
  // entre POST e PATCH — divergir dela faria o modal dizer "Editar" enquanto o
  // shell criava uma ficha nova.
  const uuidEmEdicao =
    selectedObject?.fichaUuid ||
    selectedObject?.bomUUID ||
    selectedObject?.bomUuid ||
    "";
  const ehEdicao = Boolean(uuidEmEdicao);
  const tipoDoModal = ehEdicao ? "updateBOM" : "createBOM";

  const [form, setForm] = useState({
    produtoSkuUuid: "",
    versao: "1",
    vigenciaInicio: "",
    rendimentoPorcoes: "",
    ativo: true,
  });
  const [componentes, setComponentes] = useState<Componente[]>([]);
  const [linha, setLinha] = useState<Componente>(LINHA_VAZIA);
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{
    tom: "ok" | "erro" | "atencao";
    texto: string;
  } | null>(null);

  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  /**
   * Os SKUs do token. Eles moram em `organization.organizationInfo` — é a mesma
   * travessia que a DynamicTable faz no case 1, onde `organizationInfoName` é o
   * CÓDIGO e `organizationInfoDescription` é o NOME.
   */
  const skus: any[] = useMemo(() => {
    const linhas = decryptedInfoToken?.organization?.organizationInfo ?? [];
    return linhas.map((info: any) => ({
      uuid: info.organizationInfoUuid,
      codigo: info.organizationInfoName,
      nome: info.organizationInfoDescription,
    }));
  }, [decryptedInfoToken]);

  /**
   * As outras fichas, para o componente multinível. Hoje isto vem vazio: o
   * InfoToken não traz `bomInfo`. Vazio aqui é ESTADO NORMAL — a tela diz isso
   * em vez de deixar o usuário achar que o select quebrou.
   */
  const fichas: any[] = useMemo(() => {
    const linhas = decryptedInfoToken?.bomInfo ?? [];
    return linhas
      .map((info: any) => ({
        uuid: info.bomInfoUuid,
        nome: info.bomInfoDescription || info.bomInfoName,
      }))
      .filter((ficha: any) => ficha.uuid && ficha.uuid !== uuidEmEdicao);
  }, [decryptedInfoToken, uuidEmEdicao]);

  useEffect(() => {
    // Duas origens possíveis para o objeto: a linha já achatada pela
    // DynamicTable, ou o registro cru do token com o JSON ainda em texto em
    // `bomInfoValue` — neste caso quem abre é o `parseInfoValue` do shell.
    const cru = selectedObject?.bomInfoValue;
    const valores =
      cru && typeof parseInfoValue === "function"
        ? { ...selectedObject, ...parseInfoValue(cru) }
        : selectedObject ?? {};

    // A linha da tabela guarda CÓDIGO do SKU, não uuid (case 7 do
    // DynamicTable). Sem casar código com uuid, o select do produto abriria
    // vazio numa edição — e salvar em cima trocaria o produto da ficha por
    // nada. Casar é o que preserva o dado de quem já existe.
    const porCodigo = (codigo?: string) =>
      codigo ? skus.find((sku: any) => sku.codigo === codigo)?.uuid || "" : "";

    setForm({
      produtoSkuUuid:
        valores.produtoSkuUuid || porCodigo(valores.productSkuCode),
      versao: String(valores.versao ?? valores.version ?? "1"),
      // `<input type="date">` só aceita "AAAA-MM-DD". O token grava a data como
      // ISO completo ("2026-08-06T00:00:00.000Z"): sem o corte, o campo abre
      // VAZIO numa edição, e salvar por cima apagaria a vigência.
      vigenciaInicio: String(
        valores.vigenciaInicio || valores.effectiveDate || ""
      ).slice(0, 10),
      rendimentoPorcoes: String(valores.rendimentoPorcoes ?? ""),
      ativo: (valores.ativo ?? valores.isActive) !== false,
    });

    // O dado gravado hoje é PLANO: uma linha por componente, sem ficha-pai.
    // Quando o modal abre a partir de uma dessas linhas, ela vira o primeiro (e
    // único) componente da lista. Inventar os outros componentes da ficha seria
    // inventar receita.
    if (Array.isArray(valores.componentes)) {
      setComponentes(valores.componentes);
    } else if (valores.componentSkuCode || valores.quantity) {
      setComponentes([
        {
          origem: "sku",
          componenteSkuUuid: porCodigo(valores.componentSkuCode),
          componenteBomUuid: "",
          quantidade: String(valores.quantity ?? ""),
          unidade: String(valores.unitOfMeasure ?? ""),
          percentualPerda: String(valores.wastePercentage ?? ""),
        },
      ]);
    } else {
      setComponentes([]);
    }

    setLinha(LINHA_VAZIA);
    setAviso(null);
  }, [selectedObject, parseInfoValue, skus]);

  const handleLocalChange = (campo: string, valor: any) => {
    setForm((anterior) => ({ ...anterior, [campo]: valor }));
  };

  const handleLinhaChange = (campo: keyof Componente, valor: any) => {
    setLinha((anterior) => ({ ...anterior, [campo]: valor }));
  };

  const nomeDoSku = (uuid: string) => {
    const sku = skus.find((item: any) => item.uuid === uuid);
    return sku ? `${sku.codigo} — ${sku.nome}` : uuid;
  };

  const nomeDaFicha = (uuid: string) => {
    const ficha = fichas.find((item: any) => item.uuid === uuid);
    return ficha ? ficha.nome : uuid;
  };

  const adicionarComponente = () => {
    const temSku = Boolean(linha.componenteSkuUuid);
    const temFicha = Boolean(linha.componenteBomUuid);

    // Os dois casos que o multinível torna possíveis e que não podem passar:
    // trocar de origem sem limpar a anterior deixa os dois preenchidos, e aí
    // ninguém sabe qual vale — nem esta tela, nem quem for calcular o custo.
    if (temSku && temFicha) {
      setAviso({
        tom: "erro",
        texto:
          "O componente está apontando para um SKU E para uma ficha ao mesmo " +
          "tempo. Escolha uma origem só e limpe a outra.",
      });
      return;
    }
    if (!temSku && !temFicha) {
      setAviso({
        tom: "erro",
        texto:
          "Escolha o que entra nesta linha: um SKU (insumo) ou outra ficha " +
          "técnica.",
      });
      return;
    }
    if (!linha.quantidade || Number(linha.quantidade) <= 0) {
      setAviso({ tom: "erro", texto: "A quantidade tem que ser maior que 0." });
      return;
    }
    if (!linha.unidade.trim()) {
      setAviso({
        tom: "erro",
        texto: "Informe a unidade da quantidade (kg, L, un…).",
      });
      return;
    }

    setComponentes((anteriores) => [
      ...anteriores,
      {
        ...linha,
        origem: temFicha ? "ficha" : "sku",
        unidade: linha.unidade.trim(),
      },
    ]);
    setLinha(LINHA_VAZIA);
    setAviso(null);
  };

  const removerComponente = (indice: number) => {
    setComponentes((anteriores) =>
      anteriores.filter((_, posicao) => posicao !== indice)
    );
  };

  const validar = (): string | null => {
    if (!form.produtoSkuUuid) return "Escolha o produto da ficha técnica.";
    if (!form.versao || Number(form.versao) <= 0)
      return "A versão da ficha tem que ser um número maior que 0.";
    if (!form.vigenciaInicio)
      return "Informe a data de início da vigência desta versão.";
    if (!form.rendimentoPorcoes || Number(form.rendimentoPorcoes) <= 0)
      return "Informe quantas porções esta ficha rende.";
    if (componentes.length === 0)
      return "Uma ficha técnica sem componentes não descreve nada. Adicione ao menos um.";
    return null;
  };

  const handleSubmit = async () => {
    const erro = validar();
    if (erro) {
      setAviso({ tom: "erro", texto: erro });
      return;
    }

    setSalvando(true);
    try {
      // Os números vão como NÚMERO, não como o texto do input: `versao` é
      // INTEGER e `rendimentoPorcoes` é NUMERIC no banco, e mandar "1" em vez
      // de 1 deixa a conversão por conta do driver numa query crua.
      const r = await handleSave(tipoDoModal, {
        produtoSkuUuid: form.produtoSkuUuid,
        versao: Number(form.versao),
        vigenciaInicio: form.vigenciaInicio,
        rendimentoPorcoes: Number(form.rendimentoPorcoes),
        ativo: form.ativo,
        fichaUuid: uuidEmEdicao || undefined,
        // Viaja, mas o shell descarta: não há rota que escreva em
        // `asup."FichaTecnicaItem"`. Mandar é o que deixa o dia em que a rota
        // existir ser uma mudança de uma linha — e o aviso na tela é o que
        // impede alguém de contar com isso hoje.
        componentes,
      });
      if (r && !r.ok && montado.current) {
        setAviso({ tom: r.tom || "erro", texto: r.texto });
      }
    } finally {
      if (montado.current) setSalvando(false);
    }
  };

  return ReactDOM.createPortal(
    <AlfredoStyles.ModalOverlay>
      <AlfredoStyles.ModalContent ref={refDoModal} {...propsDoDialogo} large>
        <AlfredoStyles.ModalHeader>
          <AlfredoStyles.ModalTitle id="titulo-bommodal">
            <AlfredoStyles.MaterialIcons>
              receipt_long
            </AlfredoStyles.MaterialIcons>
            {ehEdicao ? "Editar Ficha Técnica" : "Nova Ficha Técnica"}
          </AlfredoStyles.ModalTitle>
          <AlfredoStyles.ModalCloseButton onClick={handleCloseModal}>
            <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
          </AlfredoStyles.ModalCloseButton>
        </AlfredoStyles.ModalHeader>

        <AlfredoStyles.ModalBody>
          <AlfredoStyles.FormContainer>
            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Cabeçalho da ficha
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.LabelInputContainer fullWidth>
                  <AlfredoStyles.Label>Produto *</AlfredoStyles.Label>
                  <AlfredoStyles.Select
                    value={form.produtoSkuUuid}
                    onChange={(e) =>
                      handleLocalChange("produtoSkuUuid", e.target.value)
                    }
                  >
                    <option value="">Selecione o produto</option>
                    {skus.map((sku: any) => (
                      <option key={sku.uuid} value={sku.uuid}>
                        {sku.codigo} — {sku.nome}
                      </option>
                    ))}
                  </AlfredoStyles.Select>
                  {skus.length === 0 && (
                    <BOMStyles.Dica>
                      Nenhum SKU cadastrado ainda. A ficha técnica descreve um
                      SKU: cadastre o produto primeiro, na aba SKUs.
                    </BOMStyles.Dica>
                  )}
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Versão *</AlfredoStyles.Label>
                  <AlfredoStyles.Input
                    type="number"
                    min="1"
                    step="1"
                    value={form.versao}
                    onChange={(e) =>
                      handleLocalChange("versao", e.target.value)
                    }
                  />
                  <BOMStyles.Dica>
                    Receita que muda vira VERSÃO nova, não correção da antiga:
                    o custo do que já foi produzido continua sendo o da versão
                    que estava valendo.
                  </BOMStyles.Dica>
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>
                    Início da vigência *
                  </AlfredoStyles.Label>
                  <AlfredoStyles.Input
                    type="date"
                    value={form.vigenciaInicio}
                    onChange={(e) =>
                      handleLocalChange("vigenciaInicio", e.target.value)
                    }
                  />
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Rendimento *</AlfredoStyles.Label>
                  <AlfredoStyles.Input
                    type="number"
                    min="0"
                    step="0.000001"
                    value={form.rendimentoPorcoes}
                    onChange={(e) =>
                      handleLocalChange("rendimentoPorcoes", e.target.value)
                    }
                    placeholder="0,000000"
                  />
                  <BOMStyles.Dica>
                    Rende{" "}
                    {form.rendimentoPorcoes
                      ? `${form.rendimentoPorcoes} porção(ões)`
                      : "N porções"}
                    . As quantidades dos componentes abaixo são para ESTE
                    rendimento inteiro, não por porção.
                  </BOMStyles.Dica>
                </AlfredoStyles.LabelInputContainer>
              </AlfredoStyles.FormGrid>

              <AlfredoStyles.CheckboxContainer>
                <AlfredoStyles.CheckboxInput>
                  <input
                    type="checkbox"
                    checked={form.ativo}
                    onChange={(e) =>
                      handleLocalChange("ativo", e.target.checked)
                    }
                  />
                  <span className="checkmark"></span>
                </AlfredoStyles.CheckboxInput>
                <AlfredoStyles.CheckboxLabel>
                  Ficha ativa
                  <span className="tooltip">
                    Desmarque para tirar esta versão de uso sem apagá-la
                  </span>
                </AlfredoStyles.CheckboxLabel>
              </AlfredoStyles.CheckboxContainer>
            </AlfredoStyles.FormSection>

            <AlfredoStyles.FormSection>
              <BOMStyles.CabecalhoDaLista>
                <AlfredoStyles.SectionTitle>
                  Componentes
                </AlfredoStyles.SectionTitle>
                <AlfredoStyles.InfoBadge>
                  {componentes.length} componente
                  {componentes.length === 1 ? "" : "s"}
                </AlfredoStyles.InfoBadge>
              </BOMStyles.CabecalhoDaLista>

              {/* O aviso vem ANTES da lista de propósito: quem vai digitar
                  doze componentes precisa saber disto antes, não depois de
                  clicar em Salvar. */}
              <AlfredoStyles.AlertMessage $tom="erro">
                <strong>Os componentes ainda não são gravados.</strong> A tabela{" "}
                <code>asup.&quot;FichaTecnicaItem&quot;</code> existe no banco,
                mas nenhuma rota escreve nela: o salvamento manda só o cabeçalho
                da ficha (produto, versão, vigência, rendimento). A receita que
                você montar aqui <strong>se perde ao fechar o modal</strong> —
                anote em outro lugar até a rota existir.
              </AlfredoStyles.AlertMessage>

              {componentes.length === 0 ? (
                <BOMStyles.ListaVazia>
                  Nenhum componente nesta ficha ainda. Use o formulário abaixo
                  para adicionar o primeiro.
                </BOMStyles.ListaVazia>
              ) : (
                <AlfredoStyles.MiniTable>
                  <thead>
                    <tr>
                      <th>Origem</th>
                      <th>Componente</th>
                      <th>Quantidade</th>
                      <th>Unidade</th>
                      <th>% perda do processo</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {componentes.map((componente, indice) => (
                      <tr key={`${componente.origem}-${indice}`}>
                        <td>
                          {componente.origem === "ficha" ? "Ficha" : "SKU"}
                        </td>
                        <td>
                          {componente.origem === "ficha"
                            ? nomeDaFicha(componente.componenteBomUuid)
                            : nomeDoSku(componente.componenteSkuUuid)}
                        </td>
                        <td>{componente.quantidade}</td>
                        <td>{componente.unidade}</td>
                        <td>{componente.percentualPerda || "0"}%</td>
                        <td>
                          <AlfredoStyles.MiniTableAction
                            type="button"
                            title="Remover componente"
                            onClick={() => removerComponente(indice)}
                          >
                            <AlfredoStyles.MaterialIcons>
                              delete
                            </AlfredoStyles.MaterialIcons>
                          </AlfredoStyles.MiniTableAction>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </AlfredoStyles.MiniTable>
              )}
            </AlfredoStyles.FormSection>

            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Adicionar componente
              </AlfredoStyles.SectionTitle>

              <BOMStyles.EscolhaDeOrigem>
                <BOMStyles.OpcaoDeOrigem>
                  <BOMStyles.RadioDeOrigem
                    type="radio"
                    name="origemDoComponente"
                    checked={linha.origem === "sku"}
                    onChange={() => handleLinhaChange("origem", "sku")}
                  />
                  Um SKU (insumo comprado)
                </BOMStyles.OpcaoDeOrigem>
                <BOMStyles.OpcaoDeOrigem>
                  <BOMStyles.RadioDeOrigem
                    type="radio"
                    name="origemDoComponente"
                    checked={linha.origem === "ficha"}
                    onChange={() => handleLinhaChange("origem", "ficha")}
                  />
                  Outra ficha técnica (multinível)
                </BOMStyles.OpcaoDeOrigem>
              </BOMStyles.EscolhaDeOrigem>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>SKU componente</AlfredoStyles.Label>
                  <AlfredoStyles.Select
                    value={linha.componenteSkuUuid}
                    disabled={linha.origem !== "sku"}
                    onChange={(e) =>
                      handleLinhaChange("componenteSkuUuid", e.target.value)
                    }
                  >
                    <option value="">Selecione o SKU</option>
                    {skus.map((sku: any) => (
                      <option key={sku.uuid} value={sku.uuid}>
                        {sku.codigo} — {sku.nome}
                      </option>
                    ))}
                  </AlfredoStyles.Select>
                  {/* Trocar de origem NÃO apaga o que já foi escolhido — perder
                      a escolha do usuário em silêncio é pior que mostrá-la. Mas
                      o campo inativo fica desabilitado, então sem este "limpar"
                      não haveria como desfazer o "os dois preenchidos". */}
                  {linha.origem !== "sku" && linha.componenteSkuUuid && (
                    <BOMStyles.Dica>
                      Ainda apontando para {nomeDoSku(linha.componenteSkuUuid)}.{" "}
                      <AlfredoStyles.MiniTableAction
                        type="button"
                        onClick={() =>
                          handleLinhaChange("componenteSkuUuid", "")
                        }
                      >
                        limpar
                      </AlfredoStyles.MiniTableAction>
                    </BOMStyles.Dica>
                  )}
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Ficha componente</AlfredoStyles.Label>
                  <AlfredoStyles.Select
                    value={linha.componenteBomUuid}
                    disabled={linha.origem !== "ficha"}
                    onChange={(e) =>
                      handleLinhaChange("componenteBomUuid", e.target.value)
                    }
                  >
                    <option value="">Selecione a ficha</option>
                    {fichas.map((ficha: any) => (
                      <option key={ficha.uuid} value={ficha.uuid}>
                        {ficha.nome}
                      </option>
                    ))}
                  </AlfredoStyles.Select>
                  {fichas.length === 0 && (
                    <BOMStyles.Dica>
                      Nenhuma outra ficha técnica cadastrada ainda — a lista
                      vazia aqui é o estado normal da rede hoje, não um erro.
                    </BOMStyles.Dica>
                  )}
                  {linha.origem !== "ficha" && linha.componenteBomUuid && (
                    <BOMStyles.Dica>
                      Ainda apontando para {nomeDaFicha(linha.componenteBomUuid)}
                      .{" "}
                      <AlfredoStyles.MiniTableAction
                        type="button"
                        onClick={() =>
                          handleLinhaChange("componenteBomUuid", "")
                        }
                      >
                        limpar
                      </AlfredoStyles.MiniTableAction>
                    </BOMStyles.Dica>
                  )}
                </AlfredoStyles.LabelInputContainer>
              </AlfredoStyles.FormGrid>

              <BOMStyles.LinhaDeComponente>
                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Quantidade</AlfredoStyles.Label>
                  <AlfredoStyles.Input
                    type="number"
                    min="0"
                    step="0.000001"
                    value={linha.quantidade}
                    onChange={(e) =>
                      handleLinhaChange("quantidade", e.target.value)
                    }
                    placeholder="0,000000"
                  />
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Unidade</AlfredoStyles.Label>
                  <AlfredoStyles.Input
                    type="text"
                    value={linha.unidade}
                    onChange={(e) =>
                      handleLinhaChange("unidade", e.target.value)
                    }
                    placeholder="kg, L, un…"
                  />
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>
                    % de perda do processo
                  </AlfredoStyles.Label>
                  <AlfredoStyles.Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={linha.percentualPerda}
                    onChange={(e) =>
                      handleLinhaChange("percentualPerda", e.target.value)
                    }
                    placeholder="0"
                  />
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.SecondaryButton
                  type="button"
                  onClick={adicionarComponente}
                >
                  <AlfredoStyles.MaterialIcons>add</AlfredoStyles.MaterialIcons>
                  Adicionar
                </AlfredoStyles.SecondaryButton>
              </BOMStyles.LinhaDeComponente>

              {/* A mesma distinção do cabeçalho deste arquivo, dita para quem
                  está preenchendo — é aqui que o erro de custo nasce. */}
              <AlfredoStyles.AlertMessage $tom="atencao">
                <strong>% de perda do processo</strong> é o que se perde ao
                executar ESTA receita (a massa que fica na batedeira, o aparo do
                corte) e pertence a esta linha da ficha.{" "}
                <strong>Fator de rendimento</strong> é o que se perde ao preparar
                o INSUMO (1 kg de cebola com casca rende 0,85 kg limpa) e
                pertence ao SKU, não à ficha. São coisas diferentes e não se
                somam sozinhas: repetir a perda do insumo aqui conta a mesma
                perda duas vezes no custo.
              </AlfredoStyles.AlertMessage>
            </AlfredoStyles.FormSection>

            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>Custo</AlfredoStyles.SectionTitle>

              <BOMStyles.CampoCalculado>
                <span>Custo da ficha</span>
                <AlfredoStyles.InfoBadge>calculado</AlfredoStyles.InfoBadge>
                {/* Travessão, nunca um número. O custo deriva do custo dos
                    insumos, que deriva de movimento de estoque — módulo que
                    ainda não existe. Preencher isto seria inventar dado. */}
                <BOMStyles.ValorCalculado>—</BOMStyles.ValorCalculado>
              </BOMStyles.CampoCalculado>
              <BOMStyles.Dica>
                O custo da ficha não é digitado: ele deriva do custo dos insumos
                que a compõem. Enquanto não existir o módulo de movimento de
                estoque, não há de onde tirar esse custo — por isso o traço, e
                não um valor.
              </BOMStyles.Dica>
            </AlfredoStyles.FormSection>

            <AlfredoStyles.AlertMessage $tom="atencao">
              O <strong>cabeçalho</strong> desta ficha (produto, versão,
              vigência, rendimento) grava em{" "}
              <strong>suprimentos/fichas-tecnicas</strong>. A lista de fichas
              acima, porém, ainda é lida do InfoToken (<code>bomInfo</code>),
              coleção que nenhum backend produz — a ficha criada aqui{" "}
              <strong>não aparece na tabela</strong> até esta tela passar a ler
              o REST paginado. Não é o salvamento que falhou.
            </AlfredoStyles.AlertMessage>

            {aviso && (
              <AlfredoStyles.AlertMessage $tom={aviso.tom}>
                {aviso.texto}
              </AlfredoStyles.AlertMessage>
            )}
          </AlfredoStyles.FormContainer>
        </AlfredoStyles.ModalBody>

        <AlfredoStyles.ModalFooter>
          <AlfredoStyles.ButtonGroup>
            <AlfredoStyles.SecondaryButton onClick={handleCloseModal}>
              <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
              Cancelar
            </AlfredoStyles.SecondaryButton>
            <AlfredoStyles.PrimaryButton
              onClick={handleSubmit}
              disabled={salvando}
            >
              <AlfredoStyles.MaterialIcons>save</AlfredoStyles.MaterialIcons>
              {salvando
                ? "Salvando..."
                : ehEdicao
                ? "Atualizar ficha"
                : "Criar ficha"}
            </AlfredoStyles.PrimaryButton>
          </AlfredoStyles.ButtonGroup>
        </AlfredoStyles.ModalFooter>
      </AlfredoStyles.ModalContent>
    </AlfredoStyles.ModalOverlay>,
    document.body
  );
};

export default BOMModal;
